import { useState, type ComponentType } from 'react';
import type { SvgIconProps } from '@mui/material';
import { Link } from 'react-router-dom';
import type { AgentConnection, Scope } from '@cef-ai/vault-sdk';
import { AgentAvatar, Button, IconButton } from '@cere/cere-design-system';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BoltIcon from '@mui/icons-material/Bolt';
import SettingsIcon from '@mui/icons-material/Settings';
import SafetyCheckIcon from '@mui/icons-material/SafetyCheck';
import LayersIcon from '@mui/icons-material/Layers';
import GroupsIcon from '@mui/icons-material/Groups';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { marketplaceUrl } from '@/lib/marketplace/links';
import { useConfig } from '@/src/config/context';
import { displayAgentName } from '@/lib/vault/agent-display';
import { useOverflowFade } from '@/src/lib/useOverflowFade';
import { agentAnchor, ORGS_ANCHOR } from '@/src/data/anchors';
import { scopeLabelsByAgent } from '@/src/data/scope-grouping';
import type { AgentCard } from '@/src/data/agent-cards';

type IconComponent = ComponentType<SvgIconProps>;

export type VaultView = 'agents' | 'notifications' | 'usage' | 'settings' | 'scopes';

// Anchors for the inline section blocks in the one-scroll body.
const ACTIVITY_ANCHOR = 'section-activity';
const SCOPES_ANCHOR = 'section-scopes';
const BILLING_ANCHOR = 'section-billing';
const SETTINGS_ANCHOR = 'section-settings';

const NAV_COLLAPSED_KEY = 'vault:navCollapsed';

function readCollapsed(): boolean {
  try { return localStorage.getItem(NAV_COLLAPSED_KEY) === '1'; } catch { return false; }
}
function writeCollapsed(next: boolean): void {
  try { localStorage.setItem(NAV_COLLAPSED_KEY, next ? '1' : '0'); } catch { /* storage unavailable */ }
}

interface Props {
  vaultDisplayName: string;
  agentCount: number;
  scopeCount: number;
  /** Agents in shared recency order (same order as the block scroll). */
  agents?: AgentConnection[];
  agentCards?: Record<string, AgentCard>;
  scopes?: Scope[];
  selectedAnchor?: string | null;
  onSelectAnchor?: (anchor: string) => void;
}

export function VaultSidebar({
  vaultDisplayName, agentCount, scopeCount,
  agents = [], agentCards = {}, scopes = [], selectedAnchor = null, onSelectAnchor,
}: Props) {
  const config = useConfig();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const toggleCollapsed = () =>
    setCollapsed((c) => { const next = !c; writeCollapsed(next); return next; });

  const scopeLabel = scopeLabelsByAgent(agents, scopes);
  // Fade only the edge(s) of the agents list that have hidden content. Re-measure
  // when the row count or collapse state changes (both alter the scroll height).
  const agentsScrollRef = useOverflowFade<HTMLDivElement>([agents.length, collapsed]);

  // Activity is the top of the scroll; a cleared anchor lands there too.
  const activityActive = selectedAnchor === ACTIVITY_ANCHOR || selectedAnchor === null || selectedAnchor === '';

  return (
    <nav
      className={`vault-nav${collapsed ? ' vault-nav--collapsed' : ''}`}
      aria-label="Vault navigation"
    >
      <div className="vault-nav__brand">
        <span className="vault-nav__brand-mark">
          <SafetyCheckIcon sx={{ fontSize: 17, color: '#fff' }} />
        </span>
        {!collapsed && <Link to="/" className="vault-nav__brand-word">Vault</Link>}
        <IconButton
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          size="small"
          className="vault-nav__collapse"
          onClick={toggleCollapsed}
        >
          <ChevronRightIcon sx={{ fontSize: 14, transform: collapsed ? 'none' : 'rotate(180deg)' }} />
        </IconButton>
      </div>

      {!collapsed && (
        <div className="vault-nav__identity">
          <div className="vault-nav__identity-glow" aria-hidden />
          <div className="vault-nav__eyebrow">Vault</div>
          <h2 className="vault-nav__vault-name" title={vaultDisplayName}>{vaultDisplayName}</h2>
          <div className="vault-nav__stats">
            <NavStat label="Agents" value={String(agentCount)} />
            <NavStat label="Scopes" value={String(scopeCount)} />
          </div>
        </div>
      )}

      <Button
        variant="primary"
        href={marketplaceUrl(config.marketplaceBaseUrl, '/')}
        startIcon={<AutoAwesomeIcon sx={{ fontSize: 15, color: 'currentColor' }} />}
        className="vault-nav__explore"
      >
        {collapsed ? '' : 'Explore agents'}
      </Button>

      {/* Activity — first tab, jumps to the top activity feed. */}
      <div className="vault-nav__group">
        <div className="vault-nav__list">
          <NavRow
            Icon={BoltIcon}
            label="Activity"
            collapsed={collapsed}
            active={activityActive}
            onClick={() => onSelectAnchor?.(ACTIVITY_ANCHOR)}
          />
        </div>
      </div>

      {/* Agents — flat, in the same recency order as the block scroll. This is
          the one scroll region: it grows to fill the rail and scrolls its list,
          so the Manage group below stays pinned and always reachable. */}
      <div ref={agentsScrollRef} className="vault-nav__group vault-nav__group--agents">
        {!collapsed && <div className="vault-nav__label">Agents</div>}
        <div className="vault-nav__list">
          {agents.map((agent) => {
            const aAnchor = agentAnchor(agent.agentId);
            const name = agentCards[agent.agentId]?.name ?? displayAgentName(agent.agentId);
            return (
              <NavRow
                key={agent.agentId}
                avatarSeed={agent.agentId}
                avatarName={name}
                avatarUrl={agentCards[agent.agentId]?.iconUrl}
                label={name}
                caption={scopeLabel.get(agent.agentId)}
                collapsed={collapsed}
                active={selectedAnchor === aAnchor}
                onClick={() => onSelectAnchor?.(aAnchor)}
              />
            );
          })}
          {agents.length === 0 && !collapsed && (
            <div className="vault-nav__empty">No agents yet</div>
          )}
        </div>
      </div>

      {/* Manage — inline section blocks at the bottom of the scroll. */}
      <div className="vault-nav__group">
        {!collapsed && <div className="vault-nav__label">Manage</div>}
        <div className="vault-nav__list">
          <NavRow
            Icon={LayersIcon}
            label="Scopes"
            collapsed={collapsed}
            count={scopeCount}
            active={selectedAnchor === SCOPES_ANCHOR}
            onClick={() => onSelectAnchor?.(SCOPES_ANCHOR)}
          />
          <NavRow
            Icon={GroupsIcon}
            label="Organizations"
            collapsed={collapsed}
            active={selectedAnchor === ORGS_ANCHOR}
            onClick={() => onSelectAnchor?.(ORGS_ANCHOR)}
          />
          <NavRow
            Icon={SettingsIcon}
            label="Settings"
            collapsed={collapsed}
            active={selectedAnchor === SETTINGS_ANCHOR}
            onClick={() => onSelectAnchor?.(SETTINGS_ANCHOR)}
          />
          {config.paymentsEnabled && (
            <NavRow
              Icon={CreditCardIcon}
              label="Billing"
              collapsed={collapsed}
              active={selectedAnchor === BILLING_ANCHOR}
              onClick={() => onSelectAnchor?.(BILLING_ANCHOR)}
            />
          )}
        </div>
      </div>

    </nav>
  );
}

function NavRow({
  Icon, avatarSeed, avatarName, avatarUrl, label, caption, count, active, collapsed, onClick,
}: {
  Icon?: IconComponent;
  avatarSeed?: string;
  avatarName?: string;
  avatarUrl?: string;
  label: string;
  caption?: string;
  count?: number;
  active?: boolean;
  collapsed?: boolean;
  onClick: () => void;
}) {
  const cls = ['vault-nav__row', active ? 'vault-nav__row--active' : ''].filter(Boolean).join(' ');
  return (
    <button type="button" className={cls} onClick={onClick} title={collapsed ? label : undefined} aria-label={label}>
      <span className="vault-nav__row-icon">
        {avatarSeed
          ? <AgentAvatar seed={avatarSeed} name={avatarName ?? label} size={20} iconUrl={avatarUrl} />
          : Icon ? <Icon sx={{ fontSize: 15, color: 'currentColor' }} /> : null}
      </span>
      {!collapsed && (
        <span className="vault-nav__row-text">
          <span className="vault-nav__row-label">{label}</span>
          {caption && <span className="vault-nav__row-caption">{caption}</span>}
        </span>
      )}
      {!collapsed && typeof count === 'number' && <span className="vault-nav__count">{count}</span>}
    </button>
  );
}

function NavStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="vault-nav__stat">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
